import React, { useState } from 'react'
import { DatePicker, message } from 'antd'
import { Button, InputNumber } from '@surecompanies/core_components'
import dayjs from 'dayjs'
import isNil from 'lodash/isNil'
import { extractApiErrorMessage } from 'helpers/api-error-message'
import {
  InvoiceDeliveryItemDto,
  ParseWarning,
} from '../../api/dto/invoice-delivery.dto'
import { useUpdateInvoiceDeliveryOeSplitMutation } from '../../api/invoice-delivery-api-client'
import { formatAmount, formatCurrency } from './generation/utils'
import { PAYMENT_TYPE_LABELS } from './emailTemplates/invoiceTemplates'
import {
  addParts,
  OeValueSource,
  resolveOeSplit,
} from './emailTemplates/oeSplit'
import {
  DATE_INPUT_FORMATS,
  DATE_INPUT_PLACEHOLDER,
  DUE_DATE_WIRE_FORMAT,
  formatAmountInput,
  hasBeenSent,
  parseAmountInput,
  pickEdit,
} from './utils'
import {
  SplitActions,
  SplitCard,
  SplitHint,
  SplitPart,
  SplitPartLabel,
  SplitRow,
  SplitTitle,
} from './OeSplitEditor.styles'

const SOURCE_HINTS: Partial<Record<OeValueSource, string>> = {
  [OeValueSource.OE2]: 'the uploaded OE - Final Funding invoice',
}

/** `undefined` is untouched; `null` is a cleared field. */
interface SplitDraft {
  total?: number | null
  part1Amount?: number | null
  part1DueDate?: string | null
  part2Amount?: number | null
}

interface OeSplitEditorProps {
  delivery: InvoiceDeliveryItemDto
  monthDeliveries: InvoiceDeliveryItemDto[]
  /** Why part 1 could not be read off the sheet, if it could not. */
  part1Warning: ParseWarning | null
  isHistorical: boolean
  onUpdated: (delivery: InvoiceDeliveryItemDto) => void
}

const describePart1Warning = (warning: ParseWarning): string =>
  warning.kind === 'invalid' && warning.cellAddress
    ? `Couldn't read ${warning.label} from cell ${warning.cellAddress}. Type the first payment below.`
    : warning.cellAddress
      ? `${warning.label} is empty in cell ${warning.cellAddress}. Type the first payment below.`
      : `${warning.label} isn't on the sheet. Type the first payment below.`

/**
 * The OE1's Open Enrollment split: the total, both amounts and part 1's due
 * date. Part 2 falls back to the uploaded OE2, shown as a placeholder, until a
 * value is typed.
 */
const OeSplitEditor: React.FC<OeSplitEditorProps> = ({
  delivery,
  monthDeliveries,
  part1Warning,
  isHistorical,
  onUpdated,
}) => {
  const [draft, setDraft] = useState<SplitDraft>({})
  const split = resolveOeSplit(delivery, monthDeliveries)
  const mutation = useUpdateInvoiceDeliveryOeSplitMutation({
    onSuccess: (updated) => {
      message.success('Open Enrollment split saved')
      setDraft({})
      onUpdated({ ...delivery, ...updated })
    },
    onError: (err) => {
      message.error(extractApiErrorMessage(err, 'Failed to save the split'))
    },
  })

  const saved = {
    total: split.isTotalTyped ? split.total : null,
    part1Amount: split.part1Amount,
    part1DueDate: split.part1DueDate,
    part2Amount:
      split.part2AmountSource === OeValueSource.TYPED
        ? split.part2Amount
        : null,
  }
  const value = {
    total: pickEdit(draft.total, saved.total),
    part1Amount: pickEdit(draft.part1Amount, saved.part1Amount),
    part1DueDate: pickEdit(draft.part1DueDate, saved.part1DueDate),
    part2Amount: pickEdit(draft.part2Amount, saved.part2Amount),
  }
  const fallbackPart2Amount =
    split.part2AmountSource === OeValueSource.TYPED ? null : split.part2Amount
  const effectivePart2 = value.part2Amount ?? fallbackPart2Amount
  const sum = addParts(value.part1Amount, effectivePart2)

  const isDirty = Object.values(draft).some((v) => v !== undefined)
  const isLocked = isHistorical || mutation.isPending
  const setField = <K extends keyof SplitDraft>(key: K, next: SplitDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: next }))

  const handleSave = () => {
    if (isNil(value.part1DueDate)) {
      message.error('Pick the first payment due date')
      return
    }
    mutation.mutate({
      id: delivery.id,
      body: {
        totalDue: value.part1Amount,
        dueDate: value.part1DueDate,
        balanceDue: value.part2Amount,
        oeTotalDue: value.total,
      },
    })
  }

  const amountInput = (
    key: 'total' | 'part1Amount' | 'part2Amount',
    label: string,
    placeholder: number | null
  ) => (
    <InputNumber
      value={value[key] ?? undefined}
      onChange={(v) => setField(key, typeof v === 'number' ? v : null)}
      placeholder={isNil(placeholder) ? '0.00' : formatAmount(placeholder)}
      min={0}
      precision={2}
      step={0.01}
      prefix="$"
      formatter={formatAmountInput}
      parser={parseAmountInput}
      aria-label={label}
      disabled={isLocked}
    />
  )

  const amountSource = isNil(value.part2Amount)
    ? SOURCE_HINTS[split.part2AmountSource]
    : undefined
  const part2Hint = isNil(amountSource)
    ? null
    : `The second payment comes from ${amountSource}, until you type it.`
  const totalMismatch =
    !isNil(value.total) && !isNil(sum) && Math.abs(value.total - sum) >= 0.005

  return (
    <SplitCard>
      <SplitTitle>Open Enrollment split</SplitTitle>

      <SplitPart>
        <SplitPartLabel>Total due</SplitPartLabel>
        {amountInput('total', 'Open Enrollment total due', sum)}
      </SplitPart>

      <SplitPart>
        <SplitPartLabel>First payment · Wire</SplitPartLabel>
        <SplitRow>
          {amountInput('part1Amount', 'First payment amount', null)}
          <DatePicker
            value={isNil(value.part1DueDate) ? null : dayjs(value.part1DueDate)}
            onChange={(date) =>
              setField(
                'part1DueDate',
                isNil(date) ? null : date.format(DUE_DATE_WIRE_FORMAT)
              )
            }
            format={DATE_INPUT_FORMATS}
            placeholder={DATE_INPUT_PLACEHOLDER}
            allowClear={false}
            aria-label="First payment due date"
            disabled={isLocked}
          />
        </SplitRow>
      </SplitPart>

      <SplitPart>
        <SplitPartLabel>
          Second payment
          {isNil(split.part2PaymentType)
            ? ''
            : ` · ${PAYMENT_TYPE_LABELS[split.part2PaymentType]}`}
        </SplitPartLabel>
        <SplitRow>
          {amountInput(
            'part2Amount',
            'Second payment amount',
            fallbackPart2Amount
          )}
        </SplitRow>
      </SplitPart>

      {isNil(part1Warning) ? null : (
        <SplitHint $tone="warning">
          {describePart1Warning(part1Warning)}
        </SplitHint>
      )}
      {part2Hint ? <SplitHint>{part2Hint}</SplitHint> : null}
      {totalMismatch && !isNil(sum) ? (
        <SplitHint $tone="warning">
          The two payments add up to {formatCurrency(sum)}, which differs from
          the total.
        </SplitHint>
      ) : null}
      {isNil(effectivePart2) ? (
        <SplitHint $tone="warning">
          Enter the second payment amount before sending: the email announces
          it.
        </SplitHint>
      ) : null}
      {isHistorical ? (
        <SplitHint>Only the latest version can be edited.</SplitHint>
      ) : hasBeenSent(delivery) ? (
        <SplitHint>
          Already emailed, so a change here updates the records only.
        </SplitHint>
      ) : null}

      <SplitActions>
        <Button
          type="primary"
          onClick={handleSave}
          loading={mutation.isPending}
          disabled={isHistorical || !isDirty}
        >
          Save
        </Button>
      </SplitActions>
    </SplitCard>
  )
}

export { OeSplitEditor }
